import VisitCrmSelector from '../VisitCrmSelector';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { getSalesCustomerContext } from '../salesApi';

export const VISIT_TYPE_OPTIONS = [
    { label: 'Cliente existente (QuickBooks)', value: 'existingClient' },
    { label: 'Primera visita / cliente nuevo', value: 'prospect' },
    { label: 'Prospección de zona / recorrido', value: 'areaProspecting' }
];

export const getCustomerKey = (businessUnit, quickBooksId) =>
    businessUnit && quickBooksId ? `${businessUnit}:${quickBooksId}` : '';

const OUTCOME_LABELS = {
    noChange: 'Sin cambio',
    followUpRequired: 'Requiere seguimiento',
    quoteRequested: 'Cotización solicitada',
    sampleRequested: 'Muestra solicitada',
    opportunityIdentified: 'Oportunidad identificada',
    potentialOrder: 'Orden potencial',
    complaint: 'Queja',
    notInterested: 'No interesado',
    visitNotCompleted: 'Visita no completada'
};

const formatMoney = (amount, currency = 'USD') => {
    try {
        return new Intl.NumberFormat('es-PA', {
            style: 'currency',
            currency,
            minimumFractionDigits: 2
        }).format(Number(amount || 0));
    } catch {
        return `${currency} ${Number(amount || 0).toFixed(2)}`;
    }
};

const formatContextDate = (value) => {
    if (!value) return '—';
    const date = /^\d{4}-\d{2}-\d{2}$/.test(String(value))
        ? new Date(`${value}T12:00:00-05:00`)
        : new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat('es-PA', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'America/Panama'
    }).format(date);
};

const formatContextDateTime = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat('es-PA', {
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: 'America/Panama'
    }).format(date);
};

const CustomerContextCard = ({ context, loading, loadError, onRetry }) => {
    if (loading) {
        return (
            <div className="ventas-customer-context ventas-customer-context--loading" aria-live="polite">
                <i className="pi pi-spin pi-spinner" aria-hidden="true" />
                <span>Cargando saldo y última visita…</span>
            </div>
        );
    }
    if (loadError) {
        return (
            <div className="ventas-customer-context ventas-customer-context--error" role="alert">
                <span>{loadError}</span>
                <Button type="button" label="Reintentar" icon="pi pi-refresh" className="p-button-text p-button-sm" onClick={onRetry} />
            </div>
        );
    }
    if (!context) return null;

    const receivables = context.receivables || {};
    const lastVisit = context.lastVisit;
    return (
        <section className="ventas-customer-context" aria-label="Contexto del cliente">
            <div className="ventas-customer-context__header">
                <div>
                    <span className="ventas-customer-context__eyebrow">Contexto del cliente</span>
                    <strong>Saldo pendiente en QuickBooks</strong>
                </div>
                {context.customer?.sourceCount > 1 ? (
                    <span className="ventas-customer-context__merged">
                        <i className="pi pi-link" aria-hidden="true" />
                        Unificado en {context.customer.sourceCount} registros
                    </span>
                ) : null}
            </div>

            {receivables.invoiceCount ? (
                <>
                    <div className="ventas-customer-balance-grid">
                        {(receivables.totalsByCurrency || []).map((total) => (
                            <div className="ventas-customer-balance" key={total.currency}>
                                <span>Total por cobrar</span>
                                <strong>{formatMoney(total.totalOpen, total.currency)}</strong>
                                <small>
                                    Vencido: {formatMoney(total.totalOverdue, total.currency)}
                                </small>
                            </div>
                        ))}
                        <div className="ventas-customer-balance">
                            <span>Facturas abiertas</span>
                            <strong>{receivables.invoiceCount}</strong>
                            <small>{receivables.overdueInvoiceCount} vencidas</small>
                        </div>
                        <div className="ventas-customer-balance">
                            <span>Mayor atraso</span>
                            <strong>{receivables.oldestOverdueDays || 0} días</strong>
                            <small>
                                {receivables.nextDueDate
                                    ? `Próximo vencimiento: ${formatContextDate(receivables.nextDueDate)}`
                                    : 'Sin próximo vencimiento'}
                            </small>
                        </div>
                    </div>
                    <div className="ventas-customer-invoices" role="region" aria-label="Facturas abiertas" tabIndex="0">
                        <table>
                            <thead>
                                <tr>
                                    <th>Factura</th>
                                    <th>Unidad</th>
                                    <th>Vence</th>
                                    <th>Saldo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(receivables.invoices || []).map((invoice) => (
                                    <tr key={`${invoice.businessUnit}:${invoice.quickBooksInvoiceId}`}>
                                        <td>{invoice.invoiceNumber}</td>
                                        <td>{invoice.businessUnitLabel}</td>
                                        <td>
                                            {formatContextDate(invoice.dueDate)}
                                            {invoice.isOverdue ? (
                                                <small className="ventas-customer-overdue">
                                                    {invoice.daysOverdue} días vencida
                                                </small>
                                            ) : null}
                                        </td>
                                        <td>{formatMoney(invoice.balance, invoice.currency)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {receivables.hasMoreInvoices ? (
                        <small className="text-color-secondary block mt-2">
                            Se muestran las 5 facturas que requieren atención primero.
                        </small>
                    ) : null}
                </>
            ) : (
                <div className="ventas-customer-context__empty">
                    <i className="pi pi-check-circle" aria-hidden="true" />
                    No hay facturas con saldo pendiente en la sincronización actual.
                </div>
            )}

            <div className="ventas-customer-last-visit">
                <div className="ventas-customer-last-visit__title">
                    <strong>Última visita registrada</strong>
                    {lastVisit?.visitDate ? <span>{formatContextDate(lastVisit.visitDate)}</span> : null}
                </div>
                {lastVisit ? (
                    <div className="ventas-customer-last-visit__content">
                        <small>Registrada por {lastVisit.sellerName}</small>
                        {lastVisit.metWith ? <p><b>Se reunió con:</b> {lastVisit.metWith}</p> : null}
                        {lastVisit.summary ? <p><b>Resumen:</b> {lastVisit.summary}</p> : null}
                        {lastVisit.agreements ? <p><b>Acuerdos:</b> {lastVisit.agreements}</p> : null}
                        {lastVisit.nextSteps ? <p><b>Próximos pasos:</b> {lastVisit.nextSteps}</p> : null}
                        {lastVisit.followUpDate ? <p><b>Seguimiento:</b> {formatContextDate(lastVisit.followUpDate)}</p> : null}
                        {lastVisit.outcome ? <p><b>Resultado:</b> {OUTCOME_LABELS[lastVisit.outcome] || lastVisit.outcome}</p> : null}
                    </div>
                ) : (
                    <p className="text-color-secondary m-0">Todavía no hay una visita con reporte enviado para este cliente.</p>
                )}
            </div>

            <small className="ventas-customer-context__privacy">
                <i className="pi pi-shield" aria-hidden="true" />
                Solo se comparte el resumen comercial enviado. No incluye ubicación, fotos, borradores ni agendas de otros vendedores.
                {receivables.lastSyncedAt ? ` QuickBooks actualizado: ${formatContextDateTime(receivables.lastSyncedAt)}.` : ''}
            </small>
        </section>
    );
};

const VisitCustomerFields = ({
    form,
    customers = [],
    loadingCustomers = false,
    customerLoadError = '',
    onReloadCustomers,
    onFieldChange,
    onCustomerChange,
    error
}) => {
    const dispatch = useDispatch();
    const [customerContext, setCustomerContext] = useState(null);
    const [loadingCustomerContext, setLoadingCustomerContext] = useState(false);
    const [customerContextError, setCustomerContextError] = useState('');
    const [contextReloadKey, setContextReloadKey] = useState(0);
    const selectedCustomerKey = getCustomerKey(form.externalClientBusinessUnit, form.externalClientId);
    const customerOptions = useMemo(() => {
        if (!selectedCustomerKey || customers.some((customer) => customer.id === selectedCustomerKey)) return customers;
        return [
            {
                id: selectedCustomerKey,
                quickBooksId: form.externalClientId,
                businessUnit: form.externalClientBusinessUnit,
                displayName: form.clientName || 'Cliente guardado',
                label: form.clientName || 'Cliente guardado'
            },
            ...customers
        ];
    }, [
        customers,
        form.clientName,
        form.externalClientBusinessUnit,
        form.externalClientId,
        selectedCustomerKey
    ]);
    const customerOptionTemplate = (customer) => customer ? (
        <span>{customer.displayName || customer.label}</span>
    ) : null;
    const customerValueTemplate = (customer) => customer ? (
        <span>{customer.displayName || customer.label}</span>
    ) : <span className="text-color-secondary">Selecciona un cliente de QuickBooks</span>;

    useEffect(() => {
        let active = true;
        if (
            form.visitType !== 'existingClient' ||
            !form.externalClientBusinessUnit ||
            !form.externalClientId
        ) {
            setCustomerContext(null);
            setCustomerContextError('');
            setLoadingCustomerContext(false);
            return () => {
                active = false;
            };
        }

        setLoadingCustomerContext(true);
        setCustomerContextError('');
        getSalesCustomerContext(
            form.externalClientBusinessUnit,
            form.externalClientId,
            dispatch
        ).then((response) => {
            if (!active) return;
            if (!response?.data) {
                setCustomerContext(null);
                setCustomerContextError('No pudimos cargar el saldo y la última visita.');
                return;
            }
            setCustomerContext(response.data);
        }).finally(() => {
            if (active) setLoadingCustomerContext(false);
        });

        return () => {
            active = false;
        };
    }, [
        contextReloadKey,
        dispatch,
        form.externalClientBusinessUnit,
        form.externalClientId,
        form.visitType
    ]);

    return (
        <>
            <div className="col-12">
                <label className="block font-medium mb-2">Tipo de actividad comercial</label>
                <Dropdown
                    value={form.visitType}
                    options={VISIT_TYPE_OPTIONS}
                    onChange={(event) => onFieldChange('visitType', event.value)}
                />
                {error('visitType')}
            </div>

            {form.visitType === 'existingClient' ? (
                <div className="col-12">
                    <label className="block font-medium mb-2">Cliente de QuickBooks</label>
                    <Dropdown
                        value={selectedCustomerKey}
                        options={customerOptions}
                        optionLabel="label"
                        optionValue="id"
                        itemTemplate={customerOptionTemplate}
                        valueTemplate={(option) => customerValueTemplate(option)}
                        onChange={(event) => {
                            const customer = customerOptions.find((option) => option.id === event.value) || null;
                            onCustomerChange(customer);
                        }}
                        placeholder="Selecciona un cliente de QuickBooks"
                        filter
                        filterBy="label,displayName"
                        emptyMessage={loadingCustomers ? 'Cargando clientes...' : customerLoadError || 'No hay clientes activos disponibles'}
                        loading={loadingCustomers}
                        disabled={loadingCustomers || Boolean(customerLoadError)}
                        className="w-full"
                    />
                    {error('externalClientId')}
                    {customerLoadError ? (
                        <div className="flex align-items-center gap-2 mt-2">
                            <small className="p-error">{customerLoadError}</small>
                            {onReloadCustomers ? (
                                <Button
                                    type="button"
                                    label="Reintentar"
                                    icon="pi pi-refresh"
                                    className="p-button-text p-button-sm"
                                    onClick={onReloadCustomers}
                                />
                            ) : null}
                        </div>
                    ) : (
                        <small className="text-color-secondary block mt-1">
                            Solo puedes seleccionar clientes activos sincronizados desde QuickBooks.
                        </small>
                    )}
                    {selectedCustomerKey ? (
                        <CustomerContextCard
                            context={customerContext}
                            loading={loadingCustomerContext}
                            loadError={customerContextError}
                            onRetry={() => setContextReloadKey((current) => current + 1)}
                        />
                    ) : null}
                </div>
            ) : form.visitType === 'prospect' ? (
                <div className="col-12">
                    <VisitCrmSelector form={form} onFieldChange={onFieldChange} />
                    <label className="block font-medium mb-2">Nombre de la empresa o prospecto</label>
                    <InputText
                        value={form.prospectName}
                        maxLength={180}
                        onChange={(event) => onFieldChange('prospectName', event.target.value)}
                        placeholder="Ingresa el nombre para esta primera visita"
                    />
                    {error('prospectName')}
                    <small className="text-color-secondary block mt-1">
                        Este prospecto se guarda en Ventas y no crea automáticamente un cliente en QuickBooks.
                    </small>
                </div>
            ) : (
                <div className="col-12">
                    <label className="block font-medium mb-2">Zona, ruta o sector a recorrer</label>
                    <InputText
                        value={form.areaName}
                        maxLength={240}
                        onChange={(event) => onFieldChange('areaName', event.target.value)}
                        placeholder="Ej.: Costa del Este, Ruta Transístmica o Panamá Oeste"
                    />
                    {error('areaName')}
                    <small className="text-color-secondary block mt-1">
                        No necesitas seleccionar un cliente. Durante el recorrido podrás registrar los prospectos y oportunidades encontradas.
                    </small>
                </div>
            )}

            {form.visitType !== 'areaProspecting' ? (
                <>
                    <div className="col-12 md:col-6">
                        <label className="block font-medium mb-2">Persona de contacto</label>
                        <InputText
                            value={form.contactName}
                            maxLength={180}
                            onChange={(event) => onFieldChange('contactName', event.target.value)}
                        />
                        {error('contactName')}
                    </div>
                    <div className="col-12 md:col-6">
                        <label className="block font-medium mb-2">Teléfono</label>
                        <InputText
                            value={form.contactPhone}
                            maxLength={80}
                            onChange={(event) => onFieldChange('contactPhone', event.target.value)}
                        />
                        {error('contactPhone')}
                    </div>
                    <div className="col-12 md:col-6">
                        <label className="block font-medium mb-2">Correo electrónico</label>
                        <InputText
                            type="email"
                            value={form.contactEmail}
                            maxLength={254}
                            onChange={(event) => onFieldChange('contactEmail', event.target.value)}
                        />
                        {error('contactEmail')}
                    </div>
                    <div className="col-12 md:col-6">
                        <label className="block font-medium mb-2">Dirección</label>
                        <InputText
                            value={form.address}
                            maxLength={500}
                            onChange={(event) => onFieldChange('address', event.target.value)}
                        />
                        {error('address')}
                    </div>
                </>
            ) : null}
        </>
    );
};

export default VisitCustomerFields;
